import React from 'react';
import { wingmanLogger } from '../services/loggerService';
import { spatialCommandController } from '../spatial/spatialCommandController';
import { spatialStateMachine } from '../spatial/spatialStateMachine';
import { spatialEventBus } from '../utils/spatialBridge';

/**
 * Wingman Error Boundary
 * Prevents full application crashes by catching component-level errors.
 * Renders a recovery UI if a critical failure occurs.
 */
export class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { 
            hasError: false, 
            error: null,
            errorCount: Number(localStorage.getItem("wingman_crash_count") || 0)
        };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, info) {
        // 🛡️ FORENSIC DIAGNOSTICS (CENTRALIZED)
        wingmanLogger.error('SYSTEM_CRASH', error.message, { 
            stack: error?.stack, 
            componentStack: info?.componentStack 
        });

        // Standard crash tracking
        this.setState(prevState => ({
            errorCount: prevState.errorCount + 1
        }));

        const nextCount = this.state.errorCount + 1;
        localStorage.setItem("wingman_crash_count", nextCount.toString());
        localStorage.setItem("wingman_last_crash", new Date().toISOString());

        // 🛡️ RECOVERY: Use safe console logging to prevent "Double Crash" during require()
        console.error("[Wingman Crash Detected]", error);

        // Task 4: Auto-Disable Faulty Modules
        const stack = (error?.stack || "") + (info?.componentStack || "");
        if (stack.includes("AuraField")) {
            console.warn("[Stability] Disabling faulty module: AuraField");
            localStorage.setItem("wingman_disable_aura", "true");
        }
        if (stack.includes("Mapbox") || stack.includes("MapLayer")) {
            console.warn("[Stability] Disabling faulty module: SpatialMap");
            localStorage.setItem("wingman_disable_spatial", "true");
        }
    }

    handleReset = () => {
        // 🛡️ ATOM S1.5 — COLD BOOT RECOVERY
        console.warn("[Stability] Executing Cold Boot Recovery - Purging all spatial subsystems.");
        
        try {
            // 1. Purge Authority & Maps
            spatialCommandController.purgeAll();
            
            // 2. Reset State Machine
            spatialStateMachine.purgeAll();
            
            // 3. Purge EventBus & Locks
            spatialEventBus.purgeAll();

            // 4. Re-establish Authority Listeners
            spatialCommandController.setupListeners();
            
            // 4. Clear Forensic Storage
            localStorage.removeItem("wingman_last_location");
            localStorage.removeItem("wingman_spatial_state");
            localStorage.removeItem("wingman_map_cache");
            localStorage.removeItem("wingman_crash_count");
        } catch (err) {
            console.error("[Stability] Recovery purge failed, falling back to reload", err);
            window.location.reload();
            return;
        }

        if (this.state.errorCount >= 3) {
            console.warn("[Stability] Circuit Breaker Triggered. Redirecting to Hard Reset.");
            this.handleHardReset();
            return;
        }

        // 🛡️ R-04 — RE-INITIALIZE RUNTIME (No refresh)
        this.setState({ hasError: false, error: null });
    };

    handleHardReset = () => {
        console.warn("[Stability] Executing Hard Reset - Clearing all spatial caches.");
        // 🛡️ R-04 — FULL FORENSIC PURGE
        localStorage.removeItem("wingman_crash_count");
        localStorage.removeItem("wingman_last_crash");
        localStorage.removeItem("wingman_disable_aura");
        localStorage.removeItem("wingman_disable_spatial");
        localStorage.removeItem("wingman_last_location");
        localStorage.removeItem("wingman_spatial_state");
        localStorage.removeItem("wingman_map_cache");
        localStorage.removeItem("wingman_force_offline");
        
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            // 🛡️ R-05 — CIRCUIT BREAKER (STOP RETRY LOOP)
            if (this.state.errorCount >= 3) {
                return (
                    <div className="wingman-safe-mode" style={{
                        position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        backgroundColor: '#050505', color: '#ff4444', fontFamily: 'monospace', zIndex: 9999, textAlign: 'center', padding: '20px'
                    }}>
                        <h1 style={{ fontSize: '14px', letterSpacing: '4px', marginBottom: '10px' }}>[ EMERGENCY SAFE MODE ]</h1>
                        <p style={{ fontSize: '12px', opacity: 0.6, maxWidth: '400px', lineHeight: '1.5' }}>
                            Critical spatial failure detected. The 3D engine has been suspended to protect system integrity.
                        </p>
                        <div style={{ marginTop: '30px', display: 'flex', gap: '15px' }}>
                            <button onClick={this.handleHardReset} style={{ background: '#ff4444', color: 'black', border: 'none', padding: '10px 20px', fontWeight: 'bold', cursor: 'pointer', borderRadius: '4px' }}>
                                RECALIBRATE SYSTEM
                            </button>
                        </div>
                    </div>
                );
            }

            return (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#0a0a0f',
                    backgroundImage: 'radial-gradient(circle at center, #1a1a2e 0%, #0a0a0f 100%)',
                    color: 'white',
                    fontFamily: '"Inter", sans-serif',
                    zIndex: 9999
                }}>
                    <div style={{
                        padding: '40px',
                        borderRadius: '20px',
                        background: 'rgba(255, 255, 255, 0.03)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        textAlign: 'center',
                        maxWidth: '500px'
                    }}>
                        <h2 style={{ letterSpacing: '2px', color: '#00ffff', marginBottom: '20px' }}>WINGMAN RECOVERING</h2>
                        <p style={{ opacity: 0.7, lineHeight: 1.6, marginBottom: '20px' }}>
                            The spatial engine encountered a minor turbulence. 
                            The system is ready to re-initialize your luxury experience.
                        </p>
                        
                        {this.state.error && (
                            <div style={{ 
                                background: 'rgba(255, 68, 68, 0.1)', 
                                borderLeft: '3px solid #ff4444',
                                padding: '10px',
                                marginBottom: '30px',
                                fontSize: '12px',
                                textAlign: 'left',
                                color: '#ff8888',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                            }}>
                                <strong>DIAGNOSTIC:</strong> {this.state.error.message || 'Unknown Spatial Anomaly'}
                            </div>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <button 
                                onClick={this.handleReset}
                                style={{
                                    padding: '12px 30px',
                                    borderRadius: '30px',
                                    border: 'none',
                                    background: 'linear-gradient(45deg, #00ffff, #0088ff)',
                                    color: 'black',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    letterSpacing: '1px',
                                    textTransform: 'uppercase'
                                }}
                            >
                                Reset Engine
                            </button>
                            <button 
                                onClick={this.handleHardReset}
                                style={{
                                    background: 'transparent',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    color: 'rgba(255,255,255,0.5)',
                                    padding: '8px 20px',
                                    borderRadius: '20px',
                                    fontSize: '11px',
                                    cursor: 'pointer',
                                    textTransform: 'uppercase'
                                }}
                            >
                                Force Hard Reset (Clears Cache)
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
